import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { TurnstileWidget } from "../components/TurnstileWidget";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { readStickyUtm } from "../hooks/useUtmParams";

export default function RegisterPage() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = searchParams.get("next");  // e.g. "/pricing" — where we send them post-signup

  // Preserve UTM params through the signup → next-page hop so the landing
  // page can re-fire its checkout with attribution intact.
  const buildNext = () => {
    if (!next) return null;
    const utmKeys = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "plan"];
    const carried = new URLSearchParams();
    for (const k of utmKeys) {
      const v = searchParams.get(k);
      if (v) carried.set(k, v);
    }
    return carried.toString() ? `${next}?${carried.toString()}` : next;
  };
  const nextUrl = buildNext();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [tsToken, setTsToken] = useState(null);
  const [regCfg, setRegCfg] = useState(null);
  useEffect(() => { api.get("/public/registration-config").then((r) => setRegCfg(r.data)).catch(() => setRegCfg({})); }, []);

  if (user && typeof user === "object") {
    if (nextUrl) return <Navigate to={nextUrl} replace />;
    return <Navigate to={user.onboarded ? "/vault" : "/initiate/welcome"} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    setSubmitting(true);
    // Read attribution off the URL + fall back to the sticky UTM cookie set
    // on first landing view — so signup carries source data even 3 clicks later.
    const attribution = {
      utm_source: searchParams.get("utm_source") || readStickyUtm("utm_source"),
      utm_medium: searchParams.get("utm_medium") || readStickyUtm("utm_medium"),
      utm_campaign: searchParams.get("utm_campaign") || readStickyUtm("utm_campaign"),
      utm_content: searchParams.get("utm_content") || readStickyUtm("utm_content"),
      utm_term: searchParams.get("utm_term") || readStickyUtm("utm_term"),
      landing_page: readStickyUtm("landing_page") || (typeof document !== "undefined" ? document.referrer : null),
      turnstile_token: tsToken,
    };
    const res = await register(name.trim(), email.trim(), password, attribution);
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error);
      toast.error(res.error || "Registration failed");
    } else {
      toast.success("Account created. Check your email before your first AI run.");
      // If they came from pricing/starter with plan-intent + UTMs, send them
      // straight back so they can complete the £7 / £25 checkout in one click.
      navigate(nextUrl || "/initiate/welcome", { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden flex items-center justify-center p-6" data-testid="register-page">
      <div className="absolute inset-0 bg-gradient-to-br from-purple-900/10 via-black to-black" />
      <div
        className="absolute inset-0 opacity-[0.05] mix-blend-overlay pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence baseFrequency='0.85' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.6'/></svg>\")",
        }}
      />

      <div className="relative w-full max-w-md">
        <Link to="/" className="flex items-center mb-10 group" data-testid="register-home-link">
          <img
            src="/stag-vault-lockup-v2.webp"
            alt="Stag Vault · AI · Passive Income"
            className="h-14 w-auto select-none"
            draggable={false}
          />
        </Link>

        <div className="bg-[#0a0a0a]/90 backdrop-blur-xl border border-purple-500/25 p-8 md:p-10 relative">
          <div className="absolute -top-px left-0 w-16 h-px bg-gradient-to-r from-purple-500 to-transparent" />

          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-purple-400 mb-2">
            · Initiation ·
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight mb-2">
            Request access
          </h1>
          <p className="text-zinc-500 text-sm mb-8 font-mono">
            Eleven categories. Hundreds of prompts. Built for operators.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
                Your name
              </label>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Alex Stagward"
                className="w-full bg-black border border-zinc-800 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none px-4 py-3 font-mono text-sm text-white placeholder:text-zinc-700 transition-all"
                data-testid="register-name-input"
              />
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
                Email
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@domain.com"
                className="w-full bg-black border border-zinc-800 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none px-4 py-3 font-mono text-sm text-white placeholder:text-zinc-700 transition-all"
                data-testid="register-email-input"
              />
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
                Password
              </label>
              <input
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min. 6 characters"
                className="w-full bg-black border border-zinc-800 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none px-4 py-3 font-mono text-sm text-white placeholder:text-zinc-700 transition-all"
                data-testid="register-password-input"
              />
            </div>

            {error && (
              <div
                className="border border-red-500/40 bg-red-500/10 text-red-300 px-4 py-3 font-mono text-xs"
                data-testid="register-error"
              >
                {error}
              </div>
            )}

            <TurnstileWidget siteKey={regCfg?.turnstile_site_key} onToken={setTsToken} testId="register-turnstile" className="mt-2" />
            {regCfg?.turnstile_site_key && !tsToken && (
              <p className="text-xs text-zinc-500" data-testid="register-security-check">Running a quick security check…</p>
            )}
            <button
              type="submit"
              disabled={submitting || (!!regCfg?.turnstile_site_key && !tsToken)}
              className="w-full bg-transparent border border-purple-500 text-purple-300 hover:bg-purple-500 hover:text-black px-8 py-4 font-mono uppercase tracking-[0.25em] text-xs transition-all duration-300 shadow-[0_0_20px_rgba(168,85,247,0.15)] hover:shadow-[0_0_35px_rgba(168,85,247,0.6)] disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="register-submit-button"
            >
              {submitting ? "Forging credentials..." : "Initiate"}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-zinc-900 text-center">
            <p className="font-mono text-[11px] text-zinc-500 tracking-wide">
              Already initiated?{" "}
              <Link
                to="/sign-in"
                className="text-purple-400 hover:text-purple-300 underline-offset-4 hover:underline"
                data-testid="go-to-login-link"
              >
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
